import { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import axios from '../axios';
import useAccountId from './useAccountId';
import useFacilityId from './useFacilityId';
import { ADD_NOTIFICATION } from '../store/types';
import { trackPhiAction } from '../services/audit.service';

/**
 * Custom hook for sharing reports across all pages
 * 
 * @param {Object} options - Configuration options
 * @param {Function} options.downloadPdf - Function to generate PDF report (must support returnBlob option)
 * @param {Function} options.generateExcel - Optional function to generate Excel report (must support returnBlob option)
 * @param {Object} options.reportData - Data needed for PDF generation (cardFilter, filter, dbData, filterListData)
 * @param {Object} options.excelReportData - Optional data for Excel generation (used for chart dialogs)
 * @param {string} options.pageType - Page type constant (e.g., PAGE_TYPE.HOSPITAL)
 * @returns {Object} - Share report state and handlers
 * 
 * Note: The hook generates PDF as blob and sends it as attachment via email API.
 * If generateExcel is provided and excelReportData is available, Excel will be generated instead of PDF.
 * The reportData is only used for PDF generation, not sent to the API.
 */
const useShareReport = ({ downloadPdf, generateExcel = null, reportData = {}, excelReportData = null, pageType }) => {
	const dispatch = useDispatch();
	const { accountId: accountIdFromHook } = useAccountId();
	const { facilityId: facilityIdFromHook } = useFacilityId();
	const authState = useSelector((state) => state.auth);

	// Share report dialog state
	const [shareReportState, setShareReportState] = useState({
		open: false,
		reportTitle: null,
		reportType: null,
		isTotalCard: false,
		isSharing: false,
		useExcel: false // Flag to indicate if Excel should be used (for chart dialogs)
	});

	// Users for sharing
	const [users, setUsers] = useState([]);

	// Available integrations (Microsoft, Google)
	const availableIntegrations = {
		microsoft: {
			connected: authState?.microsoft?.connected,
			email: authState?.microsoft?.email,
		},
		google: {
			connected: authState?.google?.connected,
			email: authState?.google?.email,
		},
	};

	// Fetch users for sharing
	const getUsers = useCallback(async () => {
		if (!accountIdFromHook) return;
		try {
			const response = await axios
				.get(`/api/user/notes/${accountIdFromHook}`)
				.catch((e) => e);
			setUsers(response?.data || []);
		} catch (error) {
			console.error('Error fetching users:', error);
			setUsers([]);
		}
	}, [accountIdFromHook]);

	// Fetch users on mount and when accountId changes
	useEffect(() => {
		getUsers();
	}, [getUsers]);

	// Handler to open share report dialog
	// Can accept: (title, type, isTotalCard, useExcel) or (title, options) where options is { type, isTotalCard, useExcel, excelData }
	const handleOnClickShareReport = useCallback((title, typeOrOptions = null, isTotalCard = false, useExcel = false) => {
		let type, isTotal, useExcelFlag, excelData = null;
		
		// Check if second parameter is an options object
		if (typeOrOptions && typeof typeOrOptions === 'object' && !Array.isArray(typeOrOptions)) {
			type = typeOrOptions.type || null;
			isTotal = typeOrOptions.isTotalCard || false;
			useExcelFlag = typeOrOptions.useExcel || false;
			excelData = typeOrOptions.excelData || null;
		} else {
			// Legacy format: (title, type, isTotalCard, useExcel)
			type = typeOrOptions;
			isTotal = isTotalCard;
			useExcelFlag = useExcel;
		}
		
		setShareReportState({
			open: true,
			reportTitle: title,
			reportType: type,
			isTotalCard: isTotal,
			isSharing: false,
			useExcel: useExcelFlag,
			excelData: excelData
		});
	}, []);

	// Handler to close share report dialog
	const handleCloseShareReport = useCallback(() => {
			setShareReportState({
				open: false,
				reportTitle: null,
				reportType: null,
				isTotalCard: false,
				isSharing: false,
				useExcel: false,
				excelData: null
			});
	}, []);

	// Handler to submit/share the report
	const handleShareReport = useCallback(async (shareData) => {
		// Get current state before updating
		let currentState;
		setShareReportState(prev => {
			currentState = { ...prev, isSharing: true };
			return currentState;
		});

		// Check if we should use Excel (from chart dialogs) or PDF
		// Use Excel if: useExcel flag is true, generateExcel function exists, and excelData is available
		const shouldUseExcel = currentState.useExcel && generateExcel && (currentState.excelData || excelReportData);
		const excelDataToUse = currentState.excelData || excelReportData;

		if (!shouldUseExcel && !downloadPdf) {
			console.error('downloadPdf function is required');
			return;
		}

		if (shouldUseExcel && !generateExcel) {
			console.error('generateExcel function is required for Excel reports');
			return;
		}

		try {
			const reportType = currentState.reportType;
			
			// Extract string identifier for API call (ADT cards pass object, but API needs string)
			let reportTypeForApi = reportType || '';
			let isAdtCard = false;
			let customData = null;
			
			if (reportType && typeof reportType === 'object') {
				// For ADT cards, use accessor, type, or a combination as identifier
				reportTypeForApi = reportType.accessor || reportType.type || `${reportType.cardId || ''}_${reportType.childId || ''}`.replace(/^_|_$/g, '') || 'adt_card';
				
				// Check if it's an ADT card (has selectedADTTableData in reportData)
				isAdtCard = !!reportData.selectedADTTableData;
				
				// Check if it's a custom ADT card (type === 'custom' and has questionId)
				if (isAdtCard && reportType.type === 'custom' && reportType.questionId) {
					customData = {
						questionId: reportType.questionId,
						isCustomCard: true
					};
				}
			}
			
			let fileBlob;
			let fileBase64;
			let fileName;
			let fileExtension;

			if (shouldUseExcel) {
				// Generate Excel file for chart dialogs
				fileBlob = await generateExcel({
					...excelDataToUse,
					returnBlob: true // Return blob instead of downloading
				});
				fileExtension = 'xlsx';
			} else {
				// Generate PDF for regular cards
				// Build downloadPdf parameters
				const pdfParams = {
					type: reportType, // Pass full object/item for PDF generation
					title: currentState.reportTitle,
					cardFilter: reportData.cardFilter,
					filter: reportData.filter,
					dbData: reportData.dbData,
					page: pageType,
					filterListData: reportData.filterListData,
					isTotalCard: currentState.isTotalCard,
					returnBlob: true, // Return blob instead of opening
				};
				
				// Add ADT-specific parameters if it's an ADT card
				if (isAdtCard) {
					pdfParams.selectedADTTableData = reportData.selectedADTTableData;
					pdfParams.isAdt = true;
					// Determine admissionReportType - use ADT if it's a custom card, otherwise use ADMISSION
					// We'll default to ADT for all ADT cards since they're from the ADT tab
					pdfParams.admissionReportType = 'ADT'; // ADMISSION_FILTER_TYPE.ADT
					if (customData) {
						pdfParams.customData = customData;
					}
				}
				
				fileBlob = await downloadPdf(pdfParams);
				fileExtension = 'pdf';
			}

			// Convert blob to base64 for API
			const reader = new FileReader();
			fileBase64 = await new Promise((resolve, reject) => {
				reader.onloadend = () => {
					const base64String = reader.result.split(',')[1]; // Remove data:mimeType;base64, prefix
					resolve(base64String);
				};
				reader.onerror = reject;
				reader.readAsDataURL(fileBlob);
			});

			fileName = `${currentState.reportTitle || 'report'}.${fileExtension}`;

			// Call the report sharing API with file as base64
			const apiPayload = {
				reportTitle: currentState.reportTitle,
				reportType: reportTypeForApi, // Use string identifier for API
				selectedUsers: shareData.selectedUsers,
				sender: shareData.sender || 'default',
				subject: shareData.subject,
				message: shareData.message,
			};

			// Add file data - use pdfBase64/pdfFileName for backward compatibility, or add excelBase64/excelFileName
			if (shouldUseExcel) {
				apiPayload.excelBase64 = fileBase64;
				apiPayload.excelFileName = fileName;
			} else {
				apiPayload.pdfBase64 = fileBase64;
				apiPayload.pdfFileName = fileName;
			}

			const response = await axios.post('/api/report/share', apiPayload).catch((e) => e);

			// Fire-and-forget audit event for share/export
			trackPhiAction({
				action: "share",
				resourceType: "report",
				format: shouldUseExcel ? "xlsx" : "pdf",
				metadata: {
					title: currentState.reportTitle,
					reportType: reportTypeForApi,
					useExcel: !!shouldUseExcel,
					pageType,
				},
			});

			if (response?.data?.providerRevoked) {
				const labelMap = {
					microsoft: 'Microsoft 365',
					google: 'Google',
				};

				dispatch({
					type: "UPDATE_PROVIDER_CONNECTION",
					payload: {
						provider: response.data.providerRevoked,
					},
				});

				dispatch({
					type: ADD_NOTIFICATION,
					payload: {
						type: 'error',
						label: `Your ${labelMap[response.data.providerRevoked]} connection was revoked. Please reconnect it to continue sending reports.`,
						id: 'reportSendFail',
					},
				});

				// Redirect to settings to reconnect provider
				const redirectUrl = `/settings/profile?accountId=${accountIdFromHook}&facilityId=${facilityIdFromHook}&tab=email&provider=${response.data.providerRevoked}`;
				window.location.href = redirectUrl;
				return;
			}

			handleCloseShareReport();

			// Show success notification
			dispatch({
				type: ADD_NOTIFICATION,
				payload: {
					type: "success",
					label: "Report shared successfully!",
					id: "shareReportSuccess",
				},
			});
		} catch (error) {
			console.error('Error sharing report:', error);
			dispatch({
				type: ADD_NOTIFICATION,
				payload: {
					type: "error",
					label: error?.response?.data?.message || "Error while sharing report.",
					id: "shareReportError",
				},
			});
		} finally {
			setShareReportState(prev => ({ ...prev, isSharing: false }));
		}
	}, [reportData, downloadPdf, generateExcel, excelReportData, pageType, dispatch, handleCloseShareReport, accountIdFromHook, facilityIdFromHook]);

	return {
		// State
		shareReportState,
		users,
		availableIntegrations,
		accountId: accountIdFromHook,
		facilityId: facilityIdFromHook,
		
		// Handlers
		handleOnClickShareReport,
		handleCloseShareReport,
		handleShareReport,
	};
};

export default useShareReport;

